import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Button, Card, CardContent, CardMedia, Typography,
    Tabs, Tab, Grid, TablePagination, Stack, Dialog,
    DialogTitle, DialogContent, DialogContentText, DialogActions
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { getPublishedArticles, getDraftArticles, deleteArticle } from "../../../Services/ApiCalls";
import type { Article } from "../../../types";

export default function AdminArticlesList() {
    const navigate = useNavigate();
    const [tab, setTab] = React.useState<"drafts" | "published">("drafts");
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [items, setItems] = React.useState<Article[]>([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [deleteDialog, setDeleteDialog] = React.useState<{ open: boolean; article: Article | null }>({
        open: false,
        article: null,
    });
    const [deleting, setDeleting] = React.useState(false);

    const fetchData = React.useCallback(async () => {
        try {
            setLoading(true);
            const api = tab === "drafts" ? getDraftArticles : getPublishedArticles;
            // Add timestamp to prevent caching
            const timestamp = Date.now();
            const res = await api(page + 1, rowsPerPage);
            console.log(`Fetched ${tab} articles at ${timestamp}:`, res);
            setItems(res.articles || res.drafts || []);
            setTotal(res.totalItems);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [tab, page, rowsPerPage]);

    React.useEffect(() => { fetchData(); }, [fetchData]);

    // Refresh data when component becomes visible (user navigates back from editing)
    React.useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchData();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [fetchData]);

    const handleDeleteClick = (article: Article) => {
        setDeleteDialog({ open: true, article });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteDialog.article) return;
        try {
            setDeleting(true);
            await deleteArticle(deleteDialog.article.id);
            setDeleteDialog({ open: false, article: null });
            fetchData(); // Refresh the list
        } catch (error: any) {
            console.error("Error deleting article:", error);
            const errorMessage = error?.message || "Failed to delete article";
            if (errorMessage.includes("401") || errorMessage.includes("Authentication")) {
                alert("Authentication failed. Please log in again and try again.");
                // Optionally redirect to login
                // navigate("/login");
            } else {
                alert(`Failed to delete article: ${errorMessage}`);
            }
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialog({ open: false, article: null });
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }} aria-label="tabs">
                    <Tab label="Drafts" value="drafts" />
                    <Tab label="Published" value="published" />
                </Tabs>
                <Button variant="contained" onClick={() => navigate("/admin/articles/new")}>
                    New Article
                </Button>
            </Stack>

            {loading && <Typography>Loading…</Typography>}

            <Grid container spacing={2}>
                {items.map((a) => (
                    <Grid key={a.id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            {a.coverImage && (
                                <CardMedia component="img" image={a.coverImage} alt={a.title} sx={{ height: 180, objectFit: "cover" }} />
                            )}
                            <CardContent sx={{ flex: 1 }}>
                                <Typography variant="h6">{a.title}</Typography>
                                {a.excerpt && <Typography variant="body2" sx={{ mt: 1 }}>{a.excerpt}</Typography>}
                                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                                    Status: {a.status} {a.publishedAt ? ` • ${new Date(a.publishedAt).toLocaleDateString()}` : ""}
                                </Typography>
                            </CardContent>
                            <Stack direction="row" spacing={1} sx={{ p: 2, pt: 0 }}>
                                <Button variant="outlined" onClick={() => navigate(`/admin/articles/${a.id}/edit`, { state: { slug: a.slug } })}>
                                    Edit
                                </Button>
                                {a.status === "published" && (
                                    <Button variant="text" onClick={() => window.open(`/articles/${a.slug}`, "_blank")}>View</Button>
                                )}
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteClick(a)}
                                >
                                    Delete
                                </Button>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />

            <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
                <DialogTitle>Delete Article</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{deleteDialog.article?.title}"? This action cannot be undone.
                        All associated images will also be deleted from S3.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}